import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../navbar/Navbar";
import useScrollToTop from "../customHooks/useScrollToTop";
import RegisterService from "../../services/RegisterService";
import { useForm } from "react-hook-form";
import Loader from "../loader/Loader";
import axios from "axios";
import { API_URL } from "../../constant/Constant";
import Multiselect from "multiselect-react-dropdown";
import toast from "react-hot-toast";

export default function EditAgent() {
  useScrollToTop();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState({
    managers: [],
    products: [],
  });
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [managerTouched, setManagerTouched] = useState(false);
  const [productTouched, setProductTouched] = useState(false);
  const [active, setActive] = useState(1);
  const managerMultiSelectRef = useRef(null);
  const productMultiSelectRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      location: "",
      manager: [],
      productAccess: [],
      active: 1,
    },
  });

  // Redirect if no user data
  useEffect(() => {
    if (!user) {
      toast.error("No user data provided. Please try again.");
      navigate("/userList");
    }
  }, [user, navigate]);

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}api/v1/getDropdownOptionsForManagerAndAgents`
        );
        setDropdownOptions({
          managers: Array.isArray(response.data.managers) ? response.data.managers : [],
          products: Array.isArray(response.data.products) ? response.data.products : [],
        });
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        setMessage("Failed to load dropdown options. Please try again later.");
        setDropdownOptions({
          managers: [],
          products: [],
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchDropdownOptions();
  }, []);

  // Autobind user data
  useEffect(() => {
    if (user) {
      setValue("name", user.agentName || "N/A");
      setValue("email", user.emailId || "");
      setValue("location", user.location || "");
      setValue("active", user.active !== undefined ? user.active : 1);
      setActive(user.active !== undefined ? user.active : 1);

      if (user.managerIds && Array.isArray(user.managerIds) && dropdownOptions.managers.length > 0) {
        const managerIds = user.managerIds.filter((id) => !isNaN(id));
        const selectedManagerObjects = dropdownOptions.managers
          .filter((manager) => managerIds.includes(manager.managerId))
          .map((manager) => ({
            name: manager.managerName || "Unknown Manager",
            id: manager.managerId,
          }));
        setSelectedManagers(selectedManagerObjects);
        setValue("manager", managerIds);
        setManagerTouched(true);
      } else {
        setSelectedManagers([]);
        setValue("manager", []);
      }

      if (
        user.productAccessOptionIds &&
        Array.isArray(user.productAccessOptionIds) &&
        dropdownOptions.products.length > 0
      ) {
        const productIds = user.productAccessOptionIds.filter((id) => !isNaN(id));
        const selectedProductObjects = dropdownOptions.products
          .filter((product) => productIds.includes(product.productAccessOptionId))
          .map((product) => ({
            name: product.productAccessOptionName || "Unknown Product",
            id: product.productAccessOptionId,
          }));
        setSelectedProducts(selectedProductObjects);
        setValue("productAccess", productIds);
        setProductTouched(true);
      } else {
        setSelectedProducts([]);
        setValue("productAccess", []);
      }
    }
  }, [user, dropdownOptions, setValue]);

  // Sync selected managers
  useEffect(() => {
    const managerIds = selectedManagers.map((manager) => manager.id);
    setValue("manager", managerIds);
    if (managerTouched) {
      if (managerIds.length > 0) {
        clearErrors("manager");
      } else {
        setError("manager", {
          type: "manual",
          message: "At least one manager is required",
        });
      }
    }
  }, [selectedManagers, managerTouched, setValue, setError, clearErrors]);

  // Sync selected products
  useEffect(() => {
    const productIds = selectedProducts.map((product) => product.id);
    setValue("productAccess", productIds);
    if (productTouched) {
      if (productIds.length > 0) {
        clearErrors("productAccess");
      } else {
        setError("productAccess", {
          type: "manual",
          message: "At least one product access option is required",
        });
      }
    }
  }, [selectedProducts, productTouched, setValue, setError, clearErrors]);

  const handleManagerInteraction = (selectedList) => {
    setSelectedManagers(selectedList);
    if (!managerTouched) {
      setManagerTouched(true);
    }
  };

  const handleProductInteraction = (selectedList) => {
    setSelectedProducts(selectedList);
    if (!productTouched) {
      setProductTouched(true);
    }
  };

  const handleActiveChange = (value) => {
    setActive(value);
    setValue("active", value);
    if (value !== undefined) {
      clearErrors("active");
    }
  };

  const onSubmit = async (data) => {
    if (data.manager.length === 0) {
      setError("manager", {
        type: "manual",
        message: "At least one manager is required",
      });
      setManagerTouched(true);
      return;
    }
    if (data.productAccess.length === 0) {
      setError("productAccess", {
        type: "manual",
        message: "At least one product access option is required",
      });
      setProductTouched(true);
      return;
    }
    if (!data.location || data.location.trim() === "") {
      setError("location", {
        type: "manual",
        message: "Location is required",
      });
      return;
    }
    if (data.active === undefined) {
      setError("active", {
        type: "manual",
        message: "Active status is required",
      });
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const response = await RegisterService.updateAgent({
        agentId: user.agentId,
        emailId: data.email,
        name: data.name,
        managerIds: data.manager,
        location: data.location,
        productAccessOptionIds: data.productAccess,
        createdBy: localStorage.getItem("userId"),
        active: data.active,
      });
      setIsLoading(false);
      if (response.status === 200) {
        toast.success("Agent updated successfully");
        navigate("/userList");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Update error:", error);
      if (error.message === "Network Error") {
        setMessage("Network Error");
      } else {
        switch (error.response ? error.response.status : "") {
          case 401:
          case 403:
          case 404:
          case 406:
          case 412:
            setMessage(error.response.data.failedReason);
            break;
          case 500:
            setMessage("Server error occurred. Please try again later.");
            break;
          default:
            setMessage("Unknown error occurred. Please try again later.");
            break;
        }
      }
    }
  };

  const managerOptions = dropdownOptions.managers.length > 0
    ? dropdownOptions.managers.map((manager) => ({
        name: manager.managerName || "Unknown Manager",
        id: manager.managerId,
      }))
    : [{ name: "No managers available", id: 0 }];

  const productOptions = dropdownOptions.products.length > 0
    ? dropdownOptions.products.map((product) => ({
        name: product.productAccessOptionName || "Unknown Product",
        id: product.productAccessOptionId,
      }))
    : [{ name: "No products available", id: 0 }];

  if (isLoading) {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="flex min-h-full flex-1 flex-col justify-start px-6 py-3 lg:px-8">
        <div className="mx-auto w-full max-w-sm md:max-w-md">
          <div className="flex flex-col justify-center items-center">
            <h2 className="text-center text-2xl font-semibold leading-9 tracking-tight primary-text">
              Edit Agent
            </h2>
          </div>
        </div>
        <div className="mt-2 mx-auto w-full max-w-sm md:max-w-md">
          {message && <p className="error-text text-center">{message}</p>}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="name" className="block text-sm font-normal leading-6">
                Name*
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  {...register("name")}
                  type="text"
                  readOnly
                  className="block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-gray-200 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-normal leading-6">
                Email address*
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  {...register("email")}
                  type="email"
                  readOnly
                  placeholder="Your e-mail address"
                  autoComplete="off"
                  className="block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-gray-200 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
            <div>
              <label htmlFor="location" className="block text-sm font-normal leading-6">
                Location*
              </label>
              <div className="mt-2">
                <input
                  id="location"
                  {...register("location", {
                    required: "Location is required",
                    maxLength: {
                      value: 25,
                      message: "Location cannot be longer than 25 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z0-9\s,\/\-\—]*$/,
                      message: "Location can only contain letters, digits, spaces, (), /, -, or —",
                    },
                    validate: (value) =>
                      value.trim() !== "" || "Location cannot be empty or only whitespace",
                  })}
                  type="text"
                  placeholder="Enter location"
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.location ? "border-red-500" : ""
                  }`}
                  onBlur={() => trigger("location")}
                />
              </div>
              {errors.location && <p className="error-text">{errors.location.message}</p>}
            </div>
            <div>
              <label htmlFor="manager" className="block text-sm font-normal leading-6">
                Managers*
              </label>
              <div className="mt-2">
                <input
                  type="hidden"
                  {...register("manager", {
                    validate: (value) =>
                      value.length > 0 || "At least one manager is required",
                  })}
                />
                <Multiselect
                  options={managerOptions}
                  displayValue="name"
                  selectedValues={selectedManagers}
                  onSelect={handleManagerInteraction}
                  onRemove={handleManagerInteraction}
                  onKeyPressFn={() => setManagerTouched(true)}
                  onSearch={() => setManagerTouched(true)}
                  placeholder="Select managers"
                  ref={managerMultiSelectRef}
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.manager ? "border-red-500" : ""
                  }`}
                />
                {errors.manager && <p className="error-text">{errors.manager.message}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="productAccess" className="block text-sm font-normal leading-6">
                Product Access*
              </label>
              <div className="mt-2">
                <input
                  type="hidden"
                  {...register("productAccess", {
                    validate: (value) =>
                      value.length > 0 || "At least one product access option is required",
                  })}
                />
                <Multiselect
                  options={productOptions}
                  displayValue="name"
                  selectedValues={selectedProducts}
                  onSelect={handleProductInteraction}
                  onRemove={handleProductInteraction}
                  onKeyPressFn={() => setProductTouched(true)}
                  onSearch={() => setProductTouched(true)}
                  placeholder="Select product access"
                  ref={productMultiSelectRef}
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.productAccess ? "border-red-500" : ""
                  }`}
                />
                {errors.productAccess && <p className="error-text">{errors.productAccess.message}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-normal leading-6">
                Active*
              </label>
              <div className="mt-2 flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    {...register("active", {
                      required: "Active status is required",
                    })}
                    value={1}
                    checked={active === 1}
                    onChange={() => handleActiveChange(1)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    {...register("active", {
                      required: "Active status is required",
                    })}
                    value={0}
                    checked={active === 0}
                    onChange={() => handleActiveChange(0)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {errors.active && <p className="error-text">{errors.active.message}</p>}
            </div>
            {message && <p className="error-text">{message}</p>}
            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md cta-one px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
              >
                Update Agent
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}